import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Link } from 'react-router-dom';

interface NavItemProps {
 text: string;
 path: string;
 delay?: number;
}

const Navbar: React.FC = () => {
 const [scrolled, setScrolled] = useState(false);
 const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

 useEffect(() => {
   const handleScroll = () => {
     setScrolled(window.scrollY > 50);
   };
   window.addEventListener('scroll', handleScroll);
   return () => window.removeEventListener('scroll', handleScroll);
 }, []);

 const navItems = [
   { text: "Services", path: "/services" },
   { text: "Team", path: "/team" },
   { text: "About", path: "/about" },
   { text: "Blog", path: "/blog" }
 ];

 return (
   <nav className={`fixed w-full z-50 transition-all duration-300 ${
     scrolled ? 'bg-[#ffd54f] backdrop-blur-md shadow-lg' : 'bg-transparent'
   }`}>
     <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
       <div className="flex items-center justify-between h-20">
         {/* Logo */}
         <div className="flex-shrink-0 flex items-center animate-fade-in">
           <Link to="/" className="transition-opacity hover:opacity-80">
             <img 
               src="/groviyo.png"
               alt="Groviyo" 
               className="h-20 w-auto sm:h-24"
             />
           </Link>
         </div>

         {/* Desktop Menu */}
         <div className="hidden md:flex items-center space-x-8">
           {navItems.map((item, index) => (
             <NavItem 
               key={item.text} 
               text={item.text} 
               path={item.path}
               delay={index * 100}
             />
           ))}
           <Link 
             to="/contact"
             className={`${
               scrolled ? 'bg-[#b3ebf2]' : 'bg-[#ffd54f]'
             } text-white px-6 py-2 rounded-full hover:shadow-lg transition-all duration-300 transform hover:scale-105 opacity-0 animate-fade-slide-up`}>
             Contact Us
           </Link>
         </div>

         {/* Mobile menu button */}
         <div className="md:hidden">
           <button
             onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
             className={`p-2 rounded-md hover:bg-[#81deea]/10 transition-colors duration-300 ${
               scrolled ? 'text-[#81deea]' : 'text-[#ffd54f]'
             }`}
             aria-expanded={mobileMenuOpen}
             aria-label="Toggle menu"
           >
             {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
           </button>
         </div>
       </div>
     </div>

     {/* Mobile menu */}
     <div 
       className={`fixed inset-x-0 top-20 z-40 transition-all duration-300 transform ${
         mobileMenuOpen ? 'translate-y-0 opacity-100 visible' : '-translate-y-full opacity-0 invisible'
       }`}
     >
       <div className="px-2 pt-2 pb-3 space-y-1 bg-white/90 backdrop-blur-md shadow-lg">
         {navItems.map((item, index) => (
           <MobileNavItem 
             key={item.text} 
             text={item.text} 
             path={item.path}
             delay={index * 100}
           />
         ))}
         <Link 
           to="/contact"
           className="block w-full bg-[#ffd54f] text-white px-6 py-3 rounded-full transition-all duration-300 hover:bg-[#ffd54f]/90 text-center opacity-0 animate-fade-slide-up" 
           style={{ animationDelay: `${navItems.length * 100}ms` }}
         >
           Contact Us
         </Link>
       </div>
     </div>
   </nav>
 );
};

const NavItem: React.FC<NavItemProps> = ({ text, path, delay = 0 }) => (
 <Link 
   to={path}
   className="relative opacity-0 animate-fade-slide-up text-gray-700 hover:text-white transition-colors duration-200" 
   style={{ animationDelay: `${delay}ms` }}
 >
   <span>{text}</span>
 </Link>
);

const MobileNavItem: React.FC<NavItemProps> = ({ text, path, delay = 0 }) => (
 <Link 
   to={path}
   className="block w-full px-3 py-2 text-gray-700 hover:bg-[#81deea]/10 rounded-md opacity-0 animate-fade-slide-up"
   style={{ animationDelay: `${delay}ms` }}
 >
   <span>{text}</span>
 </Link>
);

export default Navbar;